import { evaluate, random } from 'mathjs';

import { FUNCTIONS, OPERATORS } from '@Components/MetricsTable/metrics-table-utils';

export const OPEN_PAREN = '(';
export const CLOSE_PAREN = ')';

// Sanity-checks a compiled formula (already `fetch.<var>`-prefixed) by evaluating it
// against random stand-in values for every variable it could reference. Returns an
// empty object on success, or `{ message }` with the underlying error otherwise.
export const testFormula = (
  formula: string,
  availableVariables: string[]
): { message?: string } => {
  try {
    const fakeFetch: Record<string, number> = {};
    for (const dataVarName of availableVariables) {
      fakeFetch[dataVarName] = 1000 * random(0, 100);
    }
    evaluate(formula, { fetch: fakeFetch });
    return {};
  } catch (e) {
    return { message: e.message };
  }
};

// Splits a raw (un-prefixed) formula string into meaningful tokens — numbers, operators,
// parens, known function names, and variables present in `allowedVariables` — silently
// dropping anything else. Used for live-editing, where a partially-typed variable name
// shouldn't yet count as a token.
export const tokenizeFormula = (
  formula: string,
  allowedVariables: Record<string, string>
): string[] => {
  const tokens: string[] = [];
  const tokenRegex = /(\d+\.\d+|\d+|[a-zA-Z_][a-zA-Z0-9_]*|[+\-*/^()])/g;
  let match = tokenRegex.exec(formula);
  while (match !== null) {
    const token = match[0];
    if (
      (OPERATORS as readonly string[]).includes(token) ||
      token === OPEN_PAREN ||
      token === CLOSE_PAREN ||
      !Number.isNaN(Number(token))
    ) {
      tokens.push(token);
    } else if ((FUNCTIONS as readonly string[]).includes(token)) {
      tokens.push(token);
    } else if (allowedVariables[token]) {
      tokens.push(token);
    }
    match = tokenRegex.exec(formula);
  }
  return tokens;
};

interface SuggestedNextTokens {
  availableVariables?: string[];
  functions?: string[];
  operators?: string[];
}

// Given the formula's tokens up to the cursor, suggests which tokens can validly come
// next — drives the disabled state of the operator/variable suggestion buttons.
export const suggestNextTokens = (
  tokens: string[] | undefined,
  availableVariables: string[]
): SuggestedNextTokens => {
  if (!tokens || tokens.length === 0) {
    return {
      availableVariables,
      functions: FUNCTIONS.map(fn => `${fn}(`),
      operators: [OPEN_PAREN]
    };
  }
  const lastToken = tokens[tokens.length - 1];
  const openParensCount = tokens.filter(t => t === OPEN_PAREN).length;
  const closeParensCount = tokens.filter(t => t === CLOSE_PAREN).length;
  const unbalanced = openParensCount > closeParensCount;

  if ((OPERATORS as readonly string[]).includes(lastToken) || lastToken === OPEN_PAREN) {
    return {
      // An exponent can't be followed by a variable — only a numeric literal.
      availableVariables: lastToken === '^' ? undefined : availableVariables,
      functions: FUNCTIONS.map(fn => `${fn}(`),
      operators: [OPEN_PAREN]
    };
  }

  const isVariableOrNumber =
    availableVariables.includes(lastToken) || !Number.isNaN(Number(lastToken));
  if (isVariableOrNumber || lastToken === CLOSE_PAREN) {
    return { operators: unbalanced ? [...OPERATORS, CLOSE_PAREN] : [...OPERATORS] };
  }

  if ((FUNCTIONS as readonly string[]).includes(lastToken.replace(OPEN_PAREN, ''))) {
    return { operators: [OPEN_PAREN] };
  }

  return {};
};
